import { useId, useState } from 'react';
import type { Recommendation, Source } from '../api/types';
import type { Line } from './lines';
import { TICKET_MARK } from './ornaments';
import { Wordmark } from './Wordmark';

interface Props {
  rec: Recommendation;
  line: Line;
  /** 1-based position at this station. */
  position: number;
  total: number;
  /** Set while the previous ticket is being ejected. */
  leaving: boolean;
  /** Where the rider's list came from: MAL riders don't get an AniList label. */
  source?: Source;
}

/** `24` → `24 min`, and nothing at all when AniList has no runtime for it. */
function runtime(rec: Recommendation): string {
  if (rec.duration_minutes === null) return '—';
  if (rec.episodes !== null && rec.episodes > 1) {
    const total = rec.duration_minutes * rec.episodes;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return hours > 0 ? `${hours}h ${mins ? `${mins}m` : ''}`.trim() : `${mins}m`;
  }
  return `${rec.duration_minutes}m`;
}

/** About two lines of the ticket's reason on a phone, in characters. */
const WHY_ROOM = 80;

/**
 * The "why this one" line.
 *
 * `because_of` is the model's own decomposition — for EASE the score is a plain
 * sum over the user's items, so these really are the entries that pushed this
 * title up (CONTRACT.md §2). Where the model cannot decompose its score it
 * sends an empty list and says so, rather than us inventing a reason.
 */
function why(rec: Recommendation): string {
  if (rec.because_of.length === 0) {
    return 'Scored on your list as a whole; no single entry stands behind this one.';
  }
  const titles = rec.because_of.map((b) => b.title);
  const verb = titles.length === 1 ? 'is' : 'are';
  // The reason gets two lines, which hold about this much on a phone. Rather
  // than cut a long sentence off, the last titles fold into "one more".
  let sentence = '';
  for (let shown = titles.length; shown >= 1; shown--) {
    const names = titles.slice(0, shown);
    const rest = titles.length - shown;
    if (rest) names.push(`${['one', 'two', 'three'][rest - 1] ?? rest} more`);
    const joined = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    sentence = `Because ${joined} ${verb} on your list.`;
    if (sentence.length <= WHY_ROOM) break;
  }
  return sentence;
}

/**
 * AniList's CDN serves most covers at `medium` (~100px) and `large` (~230px).
 * The API hands back `medium`; the photo prints at about 80px, so on a 2x
 * screen `large` is the one that stays sharp. Not every cover has one — some
 * older entries 404 at `large` — so the API's own URL stays behind it.
 */
function coverUrls(rec: Recommendation): string[] {
  const src = rec.cover_image_url;
  if (!src) return [];
  const large = src.replace('/cover/medium/', '/cover/large/');
  return large === src ? [src] : [large, src];
}

/**
 * The pass photo, with AniList's community average printed under it. That is
 * AniList's own 0–100 figure, not ours — the model's score never appears
 * (CONTRACT.md §1). MAL riders see it as a plain average, since their own
 * site would show a different number.
 */
function Photo({ srcs, score, label }: { srcs: string[]; score: number | null; label: string }) {
  const [attempt, setAttempt] = useState(0);
  const src = srcs[attempt];
  return (
    <figure className={`tk-photo${src ? '' : ' failed'}`}>
      <span className="frame">
        {src && <img src={src} alt="" onError={() => setAttempt((a) => a + 1)} />}
      </span>
      {score !== null && (
        <figcaption>
          <b>{label}</b> {score}%
        </figcaption>
      )}
    </figure>
  );
}

/** How many of the rider's recommendations, counted overall, get the 推し stamp. */
export const TOP_PICKS = 10;

/**
 * The 推し ("our pick") seal, inked onto tickets in the rider's top ten
 * overall. Every platform numbers its tickets from 01, so a title can be
 * PLATFORM 06's first ticket and the rider's 23rd pick, or its fourth and the
 * second; the stamp is what tells the strongest picks apart wherever they
 * land on the network.
 */
function TopStamp({ rank }: { rank: number }) {
  const id = useId();
  return (
    <div className="tk-top10" role="img" aria-label={`Top ${TOP_PICKS} pick, number ${rank} overall`}>
      <svg viewBox="-50 -50 100 100" aria-hidden="true">
        <defs>
          <path id={`${id}t`} d="M-35,0A35,35 0 0 1 35,0" />
          <path id={`${id}b`} d="M-35,0A35,35 0 0 0 35,0" />
          {/* rubber on paper: the edges bite in unevenly */}
          <filter id={`${id}i`} x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed={rank} />
            <feDisplacementMap in="SourceGraphic" scale="2.4" />
          </filter>
        </defs>
        <g filter={`url(#${id}i)`}>
          <circle r={46} className="ring" />
          <circle r={27} className="ring thin" />
          <text className="arc">
            <textPath href={`#${id}t`} startOffset="50%">TOP {TOP_PICKS} PICK</textPath>
          </text>
          <text className="arc" dy="7">
            <textPath href={`#${id}b`} startOffset="50%">
              No.{String(rank).padStart(2, '0')}
            </textPath>
          </text>
          <text className="kj" y={8}>
            推し
          </text>
        </g>
      </svg>
    </div>
  );
}

export function Ticket({ rec, line, position, total, leaving, source = 'anilist' }: Props) {
  const no = String(position).padStart(4, '0');
  const title = rec.title_english ?? rec.title_romaji ?? `#${rec.media_id}`;
  // The concept's ticket carries a Japanese line above the title. Native is the
  // honest one; romaji stands in when AniList has no native title, and the row
  // is dropped rather than repeating the title back in the same script.
  const secondary =
    rec.title_native ?? (rec.title_romaji !== title ? rec.title_romaji : null);
  const covers = coverUrls(rec);

  return (
    <article className={`ticket${leaving ? ' out' : ''}`}>
      {/* display:contents so the wrapper does not open a line box above the head */}
      <span
        style={{ display: 'contents' }}
        dangerouslySetInnerHTML={{ __html: TICKET_MARK[line.slug] }}
      />

      {rec.rank <= TOP_PICKS && <TopStamp rank={rec.rank} />}

      <div className="tk-head">
        <span>
          次 <Wordmark /> ・ {line.kanji}
        </span>
        <span>NO. {no}</span>
      </div>

      {/*
        The top of the ticket is laid out like a commuter pass: fare and name on
        the left, the holder's photo on the right — here, the show's cover. The
        photo is fixed-size, so a slow or failed image never changes the
        ticket's height and never throws off the fit-to-window scaling.
      */}
      <div className="tk-top">
        <div className="tk-main">
          {/*
            The concept prints a match percentage here. The model cannot
            honestly supply one: `score` is uncalibrated and not comparable
            between users or models (CONTRACT.md §1, §2), so a percentage would
            be a number we made up. Rank order is the one calibrated signal
            there is, so the fare block prints the rank instead.
          */}
          <div className="tk-fare">
            <span className="tk-pct">
              {String(position).padStart(2, '0')}
              <em>/{total}</em>
            </span>
            <span className="tk-fl">
              RANK
              <br />
              <b>順位</b>
            </span>
          </div>

          <div className="tk-title">
            {secondary && <div className="jp">{secondary}</div>}
            <h1>{title}</h1>
          </div>
        </div>
        {covers.length > 0 && (
          <Photo srcs={covers} score={rec.average_score} label={source === 'mal' ? 'AVG' : 'ANILIST'} />
        )}
      </div>

      <div className="tk-tags">
        {rec.genres.slice(0, 4).map((g) => (
          <span key={g}>{g.toUpperCase()}</span>
        ))}
      </div>

      <ul className="tk-grid">
        <li>
          <b>EPISODES</b>
          <span>{rec.episodes ?? '—'}</span>
        </li>
        <li>
          <b>RUNTIME</b>
          <span>{runtime(rec)}</span>
        </li>
        <li>
          <b>STUDIO</b>
          <span>{rec.studio ?? '—'}</span>
        </li>
        <li>
          <b>YEAR</b>
          <span>{rec.season_year ?? '—'}</span>
        </li>
      </ul>

      <p className="tk-why">
        <em>なぜ ・ WHY THIS ONE</em>
        <span className="tk-why-text">{why(rec)}</span>
      </p>

      <div className="tk-tear" />
      <div className="tk-stub">
        <div className="tk-bars" />
        <div className="tk-seat">
          <b>PLATFORM</b>
          <span>{line.id}</span>
        </div>
      </div>
      <div className="tk-code">
        {line.code} {line.id} {no} JP
      </div>
    </article>
  );
}
